// Browser media helpers: image resize + audio recording.

/**
 * Read a file → image → resize to fit within maxDim × maxDim → JPEG dataURL.
 */
export async function resizeImageFile(file, maxDim = 480, quality = 0.85) {
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });
  const ratio = Math.min(maxDim / img.width, maxDim / img.height, 1);
  const w = Math.round(img.width * ratio);
  const h = Math.round(img.height * ratio);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}

/** Convert a Blob to a base64 data URL. */
export function blobToDataURL(blob) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(blob);
  });
}

export function isAudioRecordingSupported() {
  return !!(typeof navigator !== "undefined" && navigator.mediaDevices?.getUserMedia && window.MediaRecorder);
}

/**
 * Start a new audio recording session. Returns an object with stop().
 * stop() resolves to a base64 data-URL string.
 */
export async function startAudioRecording() {
  if (!isAudioRecordingSupported()) throw new Error("Audio recording not supported");
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

  // Pick a mimeType the browser supports
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
  const mimeType = candidates.find((m) => window.MediaRecorder.isTypeSupported?.(m)) || "";

  const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
  const chunks = [];
  recorder.addEventListener("dataavailable", (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data);
  });

  const stopped = new Promise((resolve) => {
    recorder.addEventListener("stop", async () => {
      stream.getTracks().forEach((t) => t.stop());
      const blob = new Blob(chunks, { type: recorder.mimeType || "audio/webm" });
      const dataUrl = await blobToDataURL(blob);
      resolve(dataUrl);
    });
  });

  recorder.start();
  return {
    stop: async () => {
      if (recorder.state !== "inactive") recorder.stop();
      return stopped;
    },
    cancel: () => {
      try { recorder.stop(); } catch { /* ignore */ }
      stream.getTracks().forEach((t) => t.stop());
    },
  };
}

/** Play a base64 audio data URL. Returns a promise that resolves on `ended`. */
export function playAudioDataUrl(dataUrl) {
  return new Promise((resolve) => {
    if (!dataUrl) { resolve(); return; }
    const audio = new Audio(dataUrl);
    audio.onended = () => resolve();
    audio.onerror = () => resolve();
    audio.play().catch(() => resolve());
  });
}
